import styled, { css } from 'styled-components'
import { getFontStyle } from '@diversioteam/diversio-ds'

import {
    HeaderContainerProps,
    UserSwitcherProps,
    UserAvatarProps,
    LogoImageProps,
    DropdownUserAvatarProps,
} from './AppHeader.types'

export const HeaderContainer = styled.div<HeaderContainerProps>`
    background-color: white;
    border-bottom: 1px solid #e0e0e0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    height: 56px;
`

export const GradientBackground = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, rgba(94, 74, 227, 0.05), transparent, rgba(232, 227, 252, 0.1));
`

export const HeaderContent = styled.div<HeaderContainerProps>`
    position: relative;
    width: 100%;
    height: 56px;

    ${(props) =>
        props.$isMobile
            ? css`
                  padding: 0 16px;
              `
            : css`
                  padding: 0 24px;
              `}
`

export const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

export const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

export const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`

export const LogoImage = styled.img<LogoImageProps>`
    width: auto;
    height: 32px;
`

export const LogoText = styled.div`
    display: none;

    @media (min-width: 640px) {
        display: block;
    }
`

export const LogoSubtitle = styled.span`
    ${getFontStyle('p02Medium')} /* 14px, 500 weight - matches text-sm font-medium */
    color: #7a7a7a;
`

export const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    @media (min-width: 768px) {
        gap: 16px;
    }
`

export const UserGreeting = styled.div`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-right: 8px;
    }
`

export const GreetingText = styled.span`
    ${getFontStyle('p02Medium')}
    color: #4a4a4a;
`

export const UserSwitcherTrigger = styled.div<UserSwitcherProps>`
    display: flex;
    align-items: center;
    background: linear-gradient(to right, rgba(232, 227, 252, 0.3), rgba(232, 227, 252, 0.2));
    border: 1px solid rgba(232, 227, 252, 0.4);
    border-radius: 12px;
    cursor: pointer;
    outline: none;
    transition: all 200ms;
    touch-action: manipulation;
    min-height: 44px;

    gap: ${(props) => (props.$isMobile ? '8px' : '12px')};
    padding: ${(props) => (props.$isMobile ? '8px' : '12px 16px')};

    &:hover {
        background: rgba(232, 227, 252, 0.4);
        border-color: rgba(94, 74, 227, 0.5);
    }

    &:focus {
        outline: none;
    }
`

export const UserSwitcherContent = styled.div<UserSwitcherProps>`
    display: flex;
    align-items: center;
    gap: ${(props) => (props.$isMobile ? '4px' : '8px')};
`

export const UserAvatar = styled.div<UserAvatarProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${(props) => {
        // Map Tailwind classes to actual colors
        switch (props.$userColor) {
            case 'bg-red-500':
                return '#ef4444'
            case 'bg-primary':
                return '#6366f1' // Indigo-500
            case 'bg-teal-500':
                return '#14b8a6'
            default:
                return '#6366f1'
        }
    }};

    width: 24px;
    height: 24px;

    @media (min-width: 768px) {
        width: 32px;
        height: 32px;
    }
`

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
`

export const UserLabel = styled.span`
    ${getFontStyle('p03Medium')} /* 12px, 500 weight - matches text-xs font-medium */
    color: #7a7a7a;
`

export const UserName = styled.span`
    ${getFontStyle('p02Semibold')} /* 14px, 600 weight - matches text-sm font-semibold */
    color: #2e2e2e;
`

export const DropdownMenuItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    cursor: pointer;
    touch-action: manipulation;
`

export const DropdownUserAvatar = styled.div<DropdownUserAvatarProps>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${(props) => {
        switch (props.$bgColor) {
            case 'red':
                return '#ef4444'
            case 'primary':
                return '#6366f1' // Indigo-500
            case 'teal':
                return '#14b8a6'
            default:
                return '#6366f1'
        }
    }};
`

export const DropdownUserInfo = styled.div`
    display: flex;
    flex-direction: column;
`

export const DropdownUserName = styled.span`
    ${getFontStyle('p02Medium')}/* 14px, 500 weight - standard font-medium */
`

export const DropdownUserRole = styled.span`
    ${getFontStyle('p03Regular')} /* 12px, 400 weight - matches text-xs */
    color: #7a7a7a;
`

export const StyledDropdownMenuContent = styled.div`
    width: 192px;
`
